import { useCallback, useContext, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { isEqual } from 'lodash';
import { UserContext } from '../../../contexts/UserContext';
import {
  PROJECT_UPDATE_PROPERTY,
  PROJECT_BATCH_UPDATE_PROPERTIES,
  GET_PROJECT_PROPERTIES,
  GET_PROJECT_PROPERTIES_FOR_OBJECTS,
  ORGANIZATION_UPDATE_PROPERTY,
  ORGANIZATION_BATCH_UPDATE_PROPERTIES,
  GET_ORGANIZATION_PROPERTIES,
  USER_UPDATE_PROPERTY,
  USER_BATCH_UPDATE_PROPERTIES,
  GET_USER_PROPERTIES,
} from '../../../graphql/properties';
import { PropertyType, PropertyValueType, PropertyValueMap } from '../types';
import { propertyRegistry } from '../PropertyRegistry';

// Union type for all possible property values based on PropertyValueType
export type PropertyValueData = PropertyValueMap[PropertyValueType];

export interface PropertyValue {
  identifier: string;
  value: PropertyValueData;
  valueType: PropertyValueType;
  createdAt: string;
  updatedAt: string;
}

export interface PropertyGroup {
  propertyIdentifier: string;
  properties: PropertyValue[];
}

export interface PropertyUpdateInput {
  identifier: string;
  value: PropertyValueData;
  valueType: PropertyValueType;
  propertyIdentifier: string;
  propertyType: PropertyType;
}

/** Data for a single property update event */
export interface PropertyUpdateEvent {
  identifier: string;
  propertyIdentifier: string;
  propertyType: PropertyType;
  value: PropertyValueData;
}

/** Shape of the projectById query result for property queries */
interface ProjectPropertiesData {
  projectById?: {
    id: string;
    stateByUser?: Array<{ userId: string; properties?: PropertyGroup[] }>;
    state?: { properties?: PropertyGroup[] };
  };
}

/** Shape of the organisationById query result for property queries */
interface OrganizationPropertiesData {
  organisationById?: {
    id: string;
    properties?: PropertyGroup[];
  };
}

/** Shape of the currentUser query result for property queries */
interface UserPropertiesData {
  currentUser?: {
    id: string;
    properties?: PropertyGroup[];
  };
}

export interface UsePropertyManagerOptions {
  projectId?: string;
  organizationId?: string;
  /** Optional: specific object identifiers to fetch parameters for (more efficient) */
  objectIdentifiers?: string[];
  /** Callback fired when a property is updated (from subscription or initial load) */
  onPropertyUpdate?: (event: PropertyUpdateEvent) => void;
  /** Skip all queries (useful when property data is provided via context instead) */
  skip?: boolean;
}

export interface UsePropertyManagerReturn {
  // Data access
  getProperties: (propertyType: PropertyType) => PropertyGroup[];
  /** Returns the stored value only. Returns undefined if no value is stored — does NOT fall back to registry defaults. Use getPropertyDefaultValue() if you need the default. */
  getPropertyValue: (propertyIdentifier: string, parameterIdentifier: string, propertyType: PropertyType) => PropertyValueData | undefined;
  /** Returns the registry default value for a property, or undefined if not registered. */
  getPropertyDefaultValue: (parameterIdentifier: string) => PropertyValueData | undefined;

  // Multi-selection support
  getPropertyValues: (propertyIdentifiers: string[], parameterIdentifier: string, propertyType: PropertyType) => (PropertyValueData | undefined)[];
  hasPropertyMixedValues: (propertyIdentifiers: string[], parameterIdentifier: string, propertyType: PropertyType) => boolean;

  // Mutations
  updateProperty: (params: PropertyUpdateInput) => Promise<unknown>;
  updatePropertiesBatch: (updates: PropertyUpdateInput[]) => Promise<unknown[]>;

  // Loading states
  loading: boolean;
  updating: boolean;
}

/**
 * Unified hook for managing properties across all state bindings
 */
export const usePropertyManager = ({ projectId, organizationId, objectIdentifiers, onPropertyUpdate, skip: skipAll }: UsePropertyManagerOptions): UsePropertyManagerReturn => {
  // Get current user from context (more efficient than a separate query)
  const currentUser = useContext(UserContext);
  const currentUserId = currentUser?.id;

  // Project queries
  const useFilteredQuery = objectIdentifiers && objectIdentifiers.length > 0;
  const projectQuery = useFilteredQuery ? GET_PROJECT_PROPERTIES_FOR_OBJECTS : GET_PROJECT_PROPERTIES;
  const projectVariables = useFilteredQuery ? { projectId, propertyIdentifiers: objectIdentifiers } : { projectId };

  const { data: projectData, loading: projectLoading } = useQuery<ProjectPropertiesData>(projectQuery, {
    variables: projectVariables,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: skipAll || !projectId,
  });

  // Organization queries

  const { data: orgData, loading: orgLoading } = useQuery<OrganizationPropertiesData>(GET_ORGANIZATION_PROPERTIES, {
    variables: { organizationId },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: skipAll || !organizationId,
  });

  // User queries

  const { data: userPropsData, loading: userLoading } = useQuery<UserPropertiesData>(GET_USER_PROPERTIES, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: skipAll,
  });

  // Project mutations
  // Note: No onCompleted refetch — the reactive property system already holds the correct value
  // locally, and Pusher handles cross-client sync. Refetching after every mutation caused cascading
  // requests that overwhelmed the backend (6s → 13s → 25s+ response times).
  const [updateProjectPropertyMutation, { loading: updateProjectLoading }] = useMutation(PROJECT_UPDATE_PROPERTY);

  const [updateProjectPropertiesBatchMutation, { loading: batchUpdateProjectLoading }] = useMutation(PROJECT_BATCH_UPDATE_PROPERTIES);

  // Organization mutations
  const [updateOrgPropertyMutation, { loading: updateOrgLoading }] = useMutation(ORGANIZATION_UPDATE_PROPERTY);

  const [updateOrgPropertiesBatchMutation, { loading: batchUpdateOrgLoading }] = useMutation(ORGANIZATION_BATCH_UPDATE_PROPERTIES);

  // User mutations
  const [updateUserPropertyMutation, { loading: updateUserLoading }] = useMutation(USER_UPDATE_PROPERTY);

  const [updateUserPropertiesBatchMutation, { loading: batchUpdateUserLoading }] = useMutation(USER_BATCH_UPDATE_PROPERTIES);

  // Real-time property sync is handled entirely by Pusher via useReactivePropertySync.
  // GraphQL subscriptions were removed because:
  // 1. They don't include `updatedBy`, so we can't filter our own updates (causes bounceback)
  // 2. Pusher client events already handle cross-client sync with proper clientId filtering
  // 3. Multiple usePropertyManager instances created duplicate subscription listeners

  // Helper functions for getting properties by state binding
  const getUserProperties = useCallback((): PropertyGroup[] => {
    if (!projectData?.projectById?.stateByUser || !currentUserId) return [];

    const userState = projectData.projectById.stateByUser.find((state) => state.userId === currentUserId);
    return userState?.properties || [];
  }, [projectData, currentUserId]);

  const getStateProperties = useCallback((): PropertyGroup[] => {
    return projectData?.projectById?.state?.properties || [];
  }, [projectData]);

  const getOrganizationProperties = useCallback((): PropertyGroup[] => {
    return orgData?.organisationById?.properties || [];
  }, [orgData]);

  const getUserSettingsProperties = useCallback((): PropertyGroup[] => {
    return userPropsData?.currentUser?.properties || [];
  }, [userPropsData]);

  // Note: onPropertyUpdate is intentionally NOT called from this hook anymore.
  // Real-time property sync is handled by Pusher via useReactivePropertySync,
  // which properly filters out our own updates using clientId.
  // The GraphQL subscriptions here only trigger refetches for the Apollo cache.
  void onPropertyUpdate; // Suppress unused variable warning - kept for API compatibility

  // Data accessors
  const getProperties = useCallback(
    (propertyType: PropertyType): PropertyGroup[] => {
      switch (propertyType) {
        case PropertyType.PROJECT_USER:
          return getUserProperties();
        case PropertyType.PROJECT_GLOBAL:
          return getStateProperties();
        case PropertyType.ORGANIZATION:
          return getOrganizationProperties();
        case PropertyType.USER_SETTINGS:
          return getUserSettingsProperties();
        default:
          return [];
      }
    },
    [getUserProperties, getStateProperties, getOrganizationProperties, getUserSettingsProperties],
  );

  // Pre-index property groups into Maps for O(1) lookup instead of O(N) linear scan.
  // Without this, getPropertyValue does properties.find() for every lookup — O(N²) with
  // 1000 annotations × 8 properties each = ~8 million iterations on project load.
  const indexProperties = (groups: PropertyGroup[]): Map<string, Map<string, PropertyValueData>> => {
    const index = new Map<string, Map<string, PropertyValueData>>();
    for (const group of groups) {
      const params = new Map<string, PropertyValueData>();
      for (const prop of group.properties) {
        params.set(prop.identifier, prop.value);
      }
      index.set(group.propertyIdentifier, params);
    }
    return index;
  };

  const userPropertiesIndex = useMemo(() => indexProperties(getUserProperties()), [getUserProperties]);
  const statePropertiesIndex = useMemo(() => indexProperties(getStateProperties()), [getStateProperties]);
  const orgPropertiesIndex = useMemo(() => indexProperties(getOrganizationProperties()), [getOrganizationProperties]);
  const userSettingsPropertiesIndex = useMemo(() => indexProperties(getUserSettingsProperties()), [getUserSettingsProperties]);

  const getPropertyIndex = useCallback(
    (propertyType: PropertyType): Map<string, Map<string, PropertyValueData>> => {
      switch (propertyType) {
        case PropertyType.PROJECT_USER:
          return userPropertiesIndex;
        case PropertyType.PROJECT_GLOBAL:
          return statePropertiesIndex;
        case PropertyType.ORGANIZATION:
          return orgPropertiesIndex;
        case PropertyType.USER_SETTINGS:
          return userSettingsPropertiesIndex;
        default:
          return new Map();
      }
    },
    [userPropertiesIndex, statePropertiesIndex, orgPropertiesIndex, userSettingsPropertiesIndex],
  );

  // Returns the stored value only. Returns undefined if no value is stored.
  // Does NOT fall back to registry defaults — use getPropertyDefaultValue() if you need the default.
  const getPropertyValue = useCallback(
    (propertyIdentifier: string, parameterIdentifier: string, propertyType: PropertyType): PropertyValueData | undefined => {
      // If we're using filtered query and this property wasn't requested, return undefined
      if (propertyType === PropertyType.PROJECT_USER || propertyType === PropertyType.PROJECT_GLOBAL) {
        const useFilteredQuery = objectIdentifiers && objectIdentifiers.length > 0;
        if (useFilteredQuery && !objectIdentifiers.includes(propertyIdentifier)) {
          return undefined;
        }
      }

      const index = getPropertyIndex(propertyType);
      return index.get(propertyIdentifier)?.get(parameterIdentifier);
    },
    [getPropertyIndex, objectIdentifiers],
  );

  const getPropertyDefaultValue = useCallback((parameterIdentifier: string): PropertyValueData | undefined => {
    return propertyRegistry.getConfig(parameterIdentifier)?.defaultValue;
  }, []);

  // Multi-selection support
  const getPropertyValues = useCallback(
    (propertyIdentifiers: string[], parameterIdentifier: string, propertyType: PropertyType): (PropertyValueData | undefined)[] => {
      return propertyIdentifiers.map((propertyIdentifier) => getPropertyValue(propertyIdentifier, parameterIdentifier, propertyType));
    },
    [getPropertyValue],
  );

  const hasPropertyMixedValues = useCallback(
    (propertyIdentifiers: string[], parameterIdentifier: string, propertyType: PropertyType): boolean => {
      if (propertyIdentifiers.length <= 1) return false;

      const values = getPropertyValues(propertyIdentifiers, parameterIdentifier, propertyType);

      // If we have mixed undefined and defined values, that's also a mixed state
      const definedValues = values.filter((value) => value !== undefined);
      const undefinedCount = values.length - definedValues.length;

      // If some values are undefined and some are defined, that's mixed
      if (undefinedCount > 0 && definedValues.length > 0) return true;

      // If no values are defined, it's not mixed
      if (definedValues.length === 0) return false;

      // For array values (like min-max ranges), do deep comparison
      const firstValue = definedValues[0];
      return !definedValues.every((value) => isEqual(value, firstValue));
    },
    [getPropertyValues],
  );

  // Mutation handlers
  const updateProperty = useCallback(
    async (params: PropertyUpdateInput) => {
      const { propertyType, ...updateParams } = params;

      switch (propertyType) {
        case PropertyType.PROJECT_USER:
        case PropertyType.PROJECT_GLOBAL:
          if (!projectId) throw new Error('Project ID not available for PROJECT_USER/PROJECT_GLOBAL state binding');
          return updateProjectPropertyMutation({
            variables: {
              projectId,
              identifier: params.identifier,
              value: params.value,
              valueType: params.valueType,
              propertyType: params.propertyType,
              propertyIdentifier: params.propertyIdentifier,
            },
          });
        case PropertyType.ORGANIZATION:
          if (!organizationId) throw new Error('Organization ID not available for ORGANIZATION state binding');
          return updateOrgPropertyMutation({
            variables: {
              organizationId,
              identifier: updateParams.identifier,
              value: updateParams.value,
              valueType: updateParams.valueType,
              propertyIdentifier: updateParams.propertyIdentifier,
            },
          });
        case PropertyType.USER_SETTINGS:
          return updateUserPropertyMutation({
            variables: {
              identifier: updateParams.identifier,
              value: updateParams.value,
              valueType: updateParams.valueType,
              propertyIdentifier: updateParams.propertyIdentifier,
            },
          });
        default:
          throw new Error(`Unsupported state binding: ${propertyType}`);
      }
    },
    [projectId, organizationId, updateProjectPropertyMutation, updateOrgPropertyMutation, updateUserPropertyMutation],
  );

  const updatePropertiesBatch = useCallback(
    async (updates: PropertyUpdateInput[]) => {
      // Group updates by state binding
      const projectUpdates = updates.filter((u) => u.propertyType === PropertyType.PROJECT_USER || u.propertyType === PropertyType.PROJECT_GLOBAL);
      const orgUpdates = updates.filter((u) => u.propertyType === PropertyType.ORGANIZATION);
      const userUpdates = updates.filter((u) => u.propertyType === PropertyType.USER_SETTINGS);

      const promises = [];

      if (projectUpdates.length > 0 && projectId) {
        promises.push(
          updateProjectPropertiesBatchMutation({
            variables: {
              projectId,
              updates: projectUpdates,
            },
          }),
        );
      }
      if (orgUpdates.length > 0 && organizationId) {
        promises.push(
          updateOrgPropertiesBatchMutation({
            variables: {
              organizationId,
              updates: orgUpdates.map(({ propertyType, ...update }) => update),
            },
          }),
        );
      }
      if (userUpdates.length > 0) {
        promises.push(
          updateUserPropertiesBatchMutation({
            variables: {
              updates: userUpdates.map(({ propertyType, ...update }) => update),
            },
          }),
        );
      }

      return Promise.all(promises);
    },
    [projectId, organizationId, updateProjectPropertiesBatchMutation, updateOrgPropertiesBatchMutation, updateUserPropertiesBatchMutation],
  );

  // Calculate combined loading states
  const loading = projectLoading || orgLoading || userLoading;
  const updating = updateProjectLoading || batchUpdateProjectLoading || updateOrgLoading || batchUpdateOrgLoading || updateUserLoading || batchUpdateUserLoading;

  return useMemo(() => ({
    getProperties,
    getPropertyValue,
    getPropertyDefaultValue,
    getPropertyValues,
    hasPropertyMixedValues,
    updateProperty,
    updatePropertiesBatch,
    loading,
    updating,
  }), [getProperties, getPropertyValue, getPropertyDefaultValue, getPropertyValues, hasPropertyMixedValues, updateProperty, updatePropertiesBatch, loading, updating]);
};

export default usePropertyManager;
